import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button, Spinner, Card, Container, Alert, Row, Col, Badge, Tabs, Tab, Modal } from 'react-bootstrap';
import LeaveReview from '../components/LeaveaReview';
import ContactThisBusiness from '../components/ContactThisBusiness';
import BusinessHours from '../components/BusinessHours';
import Location from '../components/Location';
import Connectwithus from '../components/Connectwithus';
import { businessApi, reviewsApi, API_BASE_URL } from '../services/api';
import { FaStar, FaHeart, FaShare, FaMapMarkerAlt, FaPhone, FaEnvelope } from 'react-icons/fa';
import RatingDistribution from '../components/RatingDistribution';
import ImageGallery from '../components/ImageGallery';
import { getBusinessPhotoUrl, getFallbackImage } from '../utils/imageUtils';
import { useAuth } from '../context/AuthContext';

const BusinessDetail = () => {
  const { id } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [similarBusinesses, setSimilarBusinesses] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const { user } = useAuth();
  const [galleryImages, setGalleryImages] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  const businessInfo = {
    facebook: 'https://facebook.com/yourpage',
    instagram: 'https://instagram.com/yourpage',
    linkedin: 'https://linkedin.com/in/yourprofile'
  };

  // Default images if no gallery images
  const defaultGalleryImages = useMemo(() => [
    {
      url: getBusinessPhotoUrl(business) || getFallbackImage('business'),
      caption: 'Business Profile'
    },
    {
      url: getFallbackImage('interior'),
      caption: 'Interior View'
    },
    {
      url: getFallbackImage('products'),
      caption: 'Products Display'
    },
    {
      url: getFallbackImage('team'),
      caption: 'Team'
    }
  ], [business]);

  // Add debug log for gallery images
  useEffect(() => {
    console.log('Current gallery images:', galleryImages);
  }, [galleryImages]);

  useEffect(() => {
    const fetchBusinessAndReviews = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        
        // Fetch business details
        const businessData = await businessApi.getById(id);
        console.log('Fetched business data:', businessData); // Debug log
        setBusiness(businessData);
        
        // Fetch reviews
        const reviewsData = await reviewsApi.getByBusinessId(id);
        setReviews(reviewsData);

        // Fetch gallery images uploaded by user
        console.log('Fetching gallery images from:', `${API_BASE_URL}/getPhotos.php?listing_id=${id}`);
        fetch(`${API_BASE_URL}/getPhotos.php?listing_id=${id}`)
          .then(res => {
            console.log('Gallery images response status:', res.status);
            return res.json();
          })
          .then(data => {
            console.log('Gallery images data:', data);
            if (data.success && Array.isArray(data.photos)) {
              const filteredImages = data.photos
                .map(img => ({
                  url: img.src,
                  caption: img.alt || ''
                }))
                .filter(img => img.url && typeof img.url === 'string' && img.url.trim() !== '');
              console.log('Filtered gallery images:', filteredImages);
              setGalleryImages(filteredImages);
            } else {
              console.log('No gallery images found or invalid response');
            }
          })
          .catch(err => {
            console.error('Error fetching gallery images:', err);
          });

        // Check if business is favorited
        const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
        setIsFavorite(favorites.includes(id));

        // Fetch similar businesses
        setSimilarBusinesses([
          { id: 1, name: 'Similar Business 1', category: 'Same Category', rating: 4.5 },
          { id: 2, name: 'Similar Business 2', category: 'Same Category', rating: 4.2 },
        ]);

      } catch (err) {
        setError(err.message || 'Failed to fetch business details.');
        console.error('Error fetching business:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessAndReviews();
  }, [id]);

  // Image upload handler
  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    setUploading(true);
    const formData = new FormData();
    for (let file of files) {
      formData.append('images[]', file);
    }
    formData.append('business_id', id);
    const res = await fetch(`${API_BASE_URL}/upload_gallery.php`, {
      method: 'POST',
      body: formData
    });
    setUploading(false);
    // Refresh gallery images after upload
    const data = await res.json();
    if (data.success) {
      // Re-fetch gallery images
      const res2 = await fetch(`${API_BASE_URL}/getPhotos.php?listing_id=${id}`);
      const data2 = await res2.json();
      if (data2.success && Array.isArray(data2.photos)) {
        const galleryImages = data2.photos.map(img => ({
          url: img.src,
          caption: img.alt || ''
        }));
        setGalleryImages(galleryImages);
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    } else {
      alert('Upload failed!');
    }
  };

  const handleBookmark = () => {
    if (business) {
      localStorage.setItem(`bookmark-${business.id}`, JSON.stringify(business));
      alert('Business bookmarked!');
    }
  };

  const toggleFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    if (isFavorite) {
      const newFavorites = favorites.filter(favId => favId !== id);
      localStorage.setItem('favorites', JSON.stringify(newFavorites));
    } else {
      favorites.push(id);
      localStorage.setItem('favorites', JSON.stringify(favorites));
    }
    setIsFavorite(!isFavorite);
  };

  const calculateRatingStats = () => {
    if (!reviews.length) return { average: 0, total: 0, distribution: {} };
    
    const total = reviews.length;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    const distribution = reviews.reduce((acc, review) => {
      acc[review.rating] = (acc[review.rating] || 0) + 1;
      return acc;
    }, {});

    return {
      average: (sum / total).toFixed(1),
      total,
      distribution
    };
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          {error}
          <Button variant="link" className="d-block mt-2" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!business) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          No business found with the specified ID.
        </Alert>
      </Container>
    );
  }

  const ratingStats = calculateRatingStats();

  return (
    <Container className="py-5">
      {/* Image Upload Option */}
      {/* <div className="mb-3">
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleImageUpload}
          ref={fileInputRef}
          disabled={uploading}
        />
        {uploading && <span style={{ marginLeft: 10 }}>Uploading...</span>}
      </div> */}
      {/* Image Gallery Grid */}
      <div className="mb-4">
        <ImageGallery images={galleryImages && galleryImages.length > 0 ? galleryImages : defaultGalleryImages} />
      </div>
      {/* Quick Actions Bar */}
      <div className="sticky-top bg-white py-2 mb-4 border-bottom">
        <Row className="align-items-center">
          <Col>
            <h1 className="h3 mb-0">{business.name}</h1>
          </Col>
          <Col xs="auto">
            {user && user.email === business.email && (
              <Link to={`/edit-business/${business.id}`}>
                <Button variant="warning" className="me-2">Edit</Button>
              </Link>
            )}
            <Button variant={isFavorite ? "danger" : "outline-danger"} onClick={toggleFavorite} className="me-2">
              <FaHeart /> {isFavorite ? 'Favorited' : 'Favorite'}
            </Button>
            <Button variant="outline-primary" onClick={() => setShowContactModal(true)}>
              <FaPhone /> Contact
            </Button>
          </Col>
        </Row>
      </div>

      <Row>
        <Col md={8}>
          {/* Main Content */}
          <Card className="mb-4">
            <Card.Body>
              <Row className="align-items-center mb-4">
                <Col>
                  <h2 className="h4 mb-0">
                    {business.name}
                    {business.verified && (
                      <Badge bg="info" className="ms-2">✔ Verified</Badge>
                    )}
                  </h2>
                  <div className="text-muted">
                    <FaMapMarkerAlt /> {business.city}, {business.state}
                  </div>
                </Col>
                <Col xs="auto">
                  <div className="d-flex align-items-center">
                    <FaStar className="text-warning" />
                    <span className="ms-1 fw-bold">{ratingStats.average}</span>
                    <span className="text-muted ms-1">({ratingStats.total} reviews)</span>
                  </div>
                </Col>
              </Row>

              {/* Add debug information */}
              {/* {process.env.NODE_ENV === 'development' && (
                <div className="mb-3 p-2 bg-light">
                  <small>Debug Info:</small>
                  <pre style={{ fontSize: '0.8rem' }}>
                    {JSON.stringify({ 
                      apiUrl: process.env.REACT_APP_API_URL,
                      businessPhoto: business?.businessPhoto,
                      profile_photo: business?.profile_photo,
                      photo: business?.photo,
                      constructedUrl: getBusinessPhotoUrl(business),
                      hasImages: galleryImages.length > 0,
                      firstImageUrl: galleryImages[0]?.url
                    }, null, 2)}
                  </pre>
                </div>
              )} */}

              <Tabs defaultActiveKey="about" className="mb-4">
                <Tab eventKey="about" title="About">
                  <Card.Text>{business.description}</Card.Text>
                  <Row className="g-3">
                    <Col md={6}>
                      <Card className="h-100">
                        <Card.Body>
                          <h5>Contact Information</h5>
                          <p><FaPhone /> {business.phone}</p>
                          <p><FaEnvelope /> {business.email}</p>
                          {business.website && (
                            <p>
                              <a href={business.website} target="_blank" rel="noopener noreferrer">
                                Visit Website
                              </a>
                            </p>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="h-100">
                        <Card.Body>
                          <h5>Business Hours</h5>
                          <BusinessHours hours={business.hours || {}} />
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Tab>
                <Tab eventKey="reviews" title={`Reviews (${reviews.length})`}>
                  {/* Replace the old rating distribution with new component */}
                  <RatingDistribution 
                    distribution={ratingStats.distribution}
                    total={ratingStats.total}
                  />
                  <LeaveReview 
                    address={business.address} 
                    business_id={business.id} 
                    reviews={reviews}
                    setReviews={setReviews}
                  />
                  {/* Show list of reviews */}
                  {reviews && reviews.length > 0 && (
                    <div className="mt-4">
                      <h4>Recent Reviews</h4>
                      {reviews.map((review, index) => (
                        <div key={index} className="card mb-3">
                          <div className="card-body">
                            <div className="d-flex justify-content-between">
                              <div className="star-rating">
                                {'⭐'.repeat(review.rating)}
                              </div>
                              <small className="text-muted">
                                {review.date ? new Date(review.date).toLocaleDateString() : ''}
                              </small>
                            </div>
                            <p className="card-text mt-2">{review.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Tab>
                <Tab eventKey="location" title="Location">
                  <Location address={business.address} />
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          {/* Sidebar */}
          <Card className="mb-4">
            <Card.Body>
              <h5>Quick Actions</h5>
              <div className="d-grid gap-2">
                <Button variant="outline-primary" onClick={handleBookmark}>
                  🔖 Save for Later
                </Button>
                <Button variant="outline-secondary" onClick={() => window.print()}>
                  🖨️ Print Details
                </Button>
                {navigator.share && (
                  <Button
                    variant="outline-info"
                    onClick={() =>
                      navigator.share({ title: business.name, url: window.location.href })
                    }
                  >
                    <FaShare /> Share
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Body>
              <h5>Similar Businesses</h5>
              {similarBusinesses.map(similar => (
                <Link 
                  key={similar.id}
                  to={`/business/${similar.id}`}
                  className="text-decoration-none"
                >
                  <Card className="mb-2">
                    <Card.Body>
                      <h6 className="mb-1">{similar.name}</h6>
                      <div className="small text-muted">
                        {similar.category} • {similar.rating} <FaStar className="text-warning" />
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              ))}
            </Card.Body>
          </Card>

          <Connectwithus address={business.address} business={businessInfo} />
        </Col>
      </Row>

      {/* Contact Modal */}
      <Modal show={showContactModal} onHide={() => setShowContactModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Contact {business.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ContactThisBusiness businessId={business.id} businessName={business.name} />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default BusinessDetail;
